import { SuccessConfetti } from "common/Icons/Icons.tsx";
import Title from "common/Typography/Title.tsx";
import Text from "common/Typography/Text.tsx";
import InvitingUserSummary from "features/configuration/InviteExternalUsers/InvitingUserSummary.tsx";
import styled, { useTheme } from "styled-components";
import NewLookExternalSuccess from "features/portfolio/CreateAccount/NewLook/NewLookExternalSuccess";
import useMedia from "utils/hooks/useMedia.ts";
import { DASHBOARD_PATH, MOBILE_SEARCH } from "common/constants.ts";
import { useContext } from "react";
import { ICON_TYPES } from "@shared/common/constants.ts";
import Flex from "common/Boxes/Flex.tsx";
import IconWithFallback from "Global/IconWithFallback.tsx";
import { AccountFormContext } from "@shared/features/common/AccountForm.context.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import Button from "common/Buttons/Button";
import { useHistory } from "react-router-dom";
import { ExternalInvitationType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const SuccessPage = () => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookExternalSuccess />;
  return <ClassicSuccessPage />;
};

const ClassicSuccessPage = () => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { createdAccounts } = useContext(AccountFormContext)!;
  const isSingleAccount = !createdAccounts || createdAccounts.length === 1;
  const { isWhitelabel, hasReadAccess, inviteData } = useContext(
    ExternalConnectAccountContext
  )!;
  const history = useHistory();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);

  return (
    <Wrapper>
      <SuccessConfettiIcon />
      <Title level={isMobile ? 2 : 1} style={{ textAlign: "center" }}>
        {isSingleAccount
          ? t("external_connect.success.title_one")
          : t("external_connect.success.title_other")}
      </Title>
      <StyledText type="secondary">
        {isSingleAccount
          ? t("external_connect.success.description_one", {
              brandPrefix: isWhitelabel ? "" : "Cornix ",
            })
          : t("external_connect.success.description_other", {
              brandPrefix: isWhitelabel ? "" : "Cornix ",
            })}
      </StyledText>
      <InvitingUserSummary hideBg={true} style={{ width: 250 }} />
      {!!createdAccounts && (
        <>
          <StyledTitle>
            {isSingleAccount
              ? t("external_connect.success.connected_accounts_one")
              : t("external_connect.success.connected_accounts_other")}
          </StyledTitle>
          <AccountsWrapper>
            {createdAccounts?.map((account, index) => (
              <Flex key={index}>
                <IconWithFallback
                  iconType={ICON_TYPES.EXCHANGE}
                  coinOrExchangeName={account.exchange}
                  iconSize={"xxl"}
                  iconStyle={{ marginRight: 8 }}
                />
                <AccountWrapper>
                  <Text>{account.exchange}</Text>
                  <AccountName type="secondary">
                    {account.client_name}
                  </AccountName>
                </AccountWrapper>
              </Flex>
            ))}
          </AccountsWrapper>
        </>
      )}
      <Text type="secondary" style={{ marginTop: 20, textAlign: "center" }}>
        {inviteData?.type === ExternalInvitationType.PERSONAL
          ? t("external_connect.success.close_tab")
          : t("external_connect.success.revoke_access")}
      </Text>
      {hasReadAccess && (
        <Button
          type="primary"
          style={{ marginTop: 20 }}
          onClick={() => history.push(DASHBOARD_PATH)}
        >
          {isWhitelabel
            ? t("external_connect.success.view_account")
            : t("external_connect.success.continue_to_cornix")}
        </Button>
      )}
    </Wrapper>
  );
};

export default SuccessPage;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 550px;
`;
const AccountsWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 12px;
  background: ${(p) => p.theme.colors.lightBackground};
  padding: 16px 20px;
  border-radius: 8px;
  width: 100%;
`;
const SuccessConfettiIcon = styled(SuccessConfetti)`
  width: 260px;
  height: auto;
  margin-bottom: 16px;
  @media (max-width: ${MOBILE_SEARCH}) {
    width: 150px;
  }
`;
const StyledTitle = styled(Title).attrs({ level: 3, type: "secondary" })`
  margin: 24px 0 12px;
  text-align: left;
  width: 100%;
`;
const AccountWrapper = styled(Flex)`
  @media (max-width: ${phoneMaxWidth}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const AccountName = styled(Text)`
  margin-left: 4px;
  @media (max-width: ${phoneMaxWidth}) {
    margin-left: 0;
  }
`;
const StyledText = styled(Text)`
  margin: 8px 0;
  text-align: center;
`;
